import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Pagination } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { VehicleCard2, TrackingHeader } from './components';
import { RegisterVehicleDialog, VehicleSettingsDialog } from '../../components/dialogs';
import { useToast } from '../../context/toastContext';
import { useTranslation } from 'react-i18next';
import { useVehicleContext } from '../../context/vehicleContext';
import { Vehicle } from '../../models/vehicle';
import { CardSkeletonLoader } from '../../components/loaders';
const Monitoring: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [viewMode, setViewMode] = useState<'cards' | 'map'>('cards');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatusTab, setSelectedStatusTab] = useState<string>('all');
  const [countryFilter, setCountryFilter] = useState<string | string[]>('all');
  const [equipmentFilter, setEquipmentFilter] = useState<string | string[]>('all');
  const [selectedTruck, setSelectedTruck] = useState<any>(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);

  const { vehicles, fetchVehicles, importVehicles, isLoading } = useVehicleContext();
  const toast = useToast();

  // Fetch vehicles with drivers and current booking on mount - only once
  useEffect(() => {
    fetchVehicles({ with_drivers: true, with_current_booking: true, include_inactive: true });
  }, []); // Empty dependency array - only run once on mount

  // Handle openVehicle URL parameter (from push notification click)
  useEffect(() => {
    const openVehicleId = searchParams.get('openVehicle');
    if (openVehicleId && vehicles.length > 0) {
      const vehicleId = parseInt(openVehicleId, 10);
      const vehicle = vehicles.find(v => v.id === vehicleId);
      if (vehicle) {
        setSelectedTruck(vehicle);
        setShowSettingsModal(true);
        // Clear the URL parameter after opening
        searchParams.delete('openVehicle');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [searchParams, vehicles, setSearchParams]);

  const handleTruckClick = (truck: Vehicle) => {
    setSelectedTruck(truck);
    setShowSettingsModal(true);
  };

  const getVehicleStatus = (vehicle: Vehicle) => {
    if (!vehicle.isActive) return 'inactive';
    if (vehicle.emptyAt) {
      const emptyDate = new Date(vehicle.emptyAt);
      const now = new Date();
      if (emptyDate < now) return 'delayed';
    }
    return 'active';
  };

  const filteredVehicles = useMemo(() => vehicles.filter(vehicle => {
    const matchesSearch = searchTerm === '' ||
      vehicle.truckPlate?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.trailerPlate?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.name?.toLowerCase().includes(searchTerm.toLowerCase());

    // Handle status filter from status tabs — inactive trucks are visible only on the Inactive tab
    const vehicleStatus = getVehicleStatus(vehicle);
    const matchesStatus = selectedStatusTab === 'inactive'
      ? vehicleStatus === 'inactive'
      : selectedStatusTab === 'all'
        ? vehicleStatus !== 'inactive'
        : vehicleStatus === selectedStatusTab;

    // Handle multi-select country filter
    const countryArray = Array.isArray(countryFilter) ? countryFilter : (countryFilter === 'all' ? [] : [countryFilter]);
    const matchesCountry = countryArray.length === 0 || countryArray.includes(vehicle.currentPosition?.country || '');

    // Handle multi-select equipment filter
    const equipmentArray = Array.isArray(equipmentFilter) ? equipmentFilter : (equipmentFilter === 'all' ? [] : [equipmentFilter]);
    const matchesEquipment = equipmentArray.length === 0 ||
      (vehicle.equipment && Array.isArray(vehicle.equipment) &&
        equipmentArray.some(eq => vehicle.equipment?.includes(eq as any)));

    return matchesSearch && matchesStatus && matchesCountry && matchesEquipment;
  }), [vehicles, searchTerm, selectedStatusTab, countryFilter, equipmentFilter]);

  const totalPages = Math.ceil(filteredVehicles.length / itemsPerPage);
  const paginatedVehicles = filteredVehicles.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const countries = useMemo(() => {
    const uniqueCountries = [...new Set(vehicles.map(v => v.currentPosition?.country).filter(Boolean))] as string[];
    return uniqueCountries.sort();
  }, [vehicles]);

  const equipmentTypes = useMemo(() => {
    const allEquipment = vehicles.flatMap(v => v.equipment || []);
    const uniqueEquipment = [...new Set(allEquipment)];
    return uniqueEquipment.sort();
  }, [vehicles]);


  // Calculate status tab counts — All excludes inactive (shown only on their own tab)
  const statusTabs = useMemo(() => {
    const active = vehicles.filter(v => getVehicleStatus(v) === 'active').length;
    const delayed = vehicles.filter(v => getVehicleStatus(v) === 'delayed').length;
    const inactive = vehicles.filter(v => getVehicleStatus(v) === 'inactive').length;
    const all = active + delayed;

    return [
      { id: 'all', label: 'All', count: all },
      { id: 'active', label: 'On route', count: active },
      { id: 'delayed', label: 'Waiting', count: delayed },
      { id: 'inactive', label: 'Inactive', count: inactive }
    ];
  }, [vehicles]);

  // Handle status tab change
  const handleStatusTabChange = (status: string) => {
    setSelectedStatusTab(status);
    setCurrentPage(1);
  };

  const handleImportTrucks = async (): Promise<void> => {
    try {
      await importVehicles();
      await fetchVehicles({ with_drivers: true, with_current_booking: true, include_inactive: true });
      toast.success(t('Trucks imported successfully!'));
    } catch {
      toast.error(t('Failed to import trucks. Please try again.'));
    }
  };

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 } }}>
      <TrackingHeader
        totalDeliveries={vehicles.length}
        statusTabs={statusTabs}
        selectedStatus={selectedStatusTab}
        onStatusChange={handleStatusTabChange}
        onAddTrack={() => setShowRegisterModal(true)}
        viewMode={viewMode}
        setViewMode={setViewMode}
        onImportTrucks={handleImportTrucks}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        countryFilter={countryFilter}
        setCountryFilter={setCountryFilter}
        equipmentFilter={equipmentFilter}
        setEquipmentFilter={setEquipmentFilter}
        countries={countries}
        equipmentTypes={equipmentTypes}
      />

      {viewMode === 'cards' && (
        <Box sx={styles.modernVehiclesGrid}>
          <CardSkeletonLoader
            showImage={false}
            showTitle={true}
            showDescription={true}
            showActions={false}
            minHeight={280}
            loading={isLoading}
            count={itemsPerPage}
            sx={{
              ...styles.vehiclesContainer,
              display: 'grid',
            }}
          >
            <Box sx={styles.vehiclesContainer}>
              {paginatedVehicles.map((vehicle) => (
                <VehicleCard2
                  key={vehicle.id}
                  vehicle={vehicle}
                  onClick={handleTruckClick}
                />
              ))}
            </Box>
          </CardSkeletonLoader>

          {totalPages > 1 && (
            <Box sx={styles.paginationContainer}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_, page) => setCurrentPage(page)}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </Box>
      )}

      {viewMode === 'map' && (
        <Box sx={styles.mapPlaceholder}>
          <Typography variant="h6" color="text.secondary">
            {t('Map view coming soon...')}
          </Typography>
        </Box>
      )}

      {filteredVehicles.length === 0 && (
        <Box sx={styles.noResults}>
          <Typography variant="h6" gutterBottom>
            {t('No trucks found')}
          </Typography>
          <Typography variant="body2">
            {t('Try adjusting your search or filters')}
          </Typography>
        </Box>
      )}

      <VehicleSettingsDialog
        open={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        truck={selectedTruck}
        onSuccess={(message) => toast.success(message)}
      />

      <RegisterVehicleDialog
        isOpen={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
        onSuccess={() => {
          fetchVehicles({ with_drivers: true, with_current_booking: true, include_inactive: true });
          toast.success(t('Truck registered successfully!'));
        }}
      />
    </Box>
  );
};

const styles = {
  modernVehiclesGrid: {
    flex: 1,
    width: '100%'
  },
  vehiclesContainer: {
    display: 'grid',
    gridTemplateColumns: {
      xs: '1fr',                    // Mobile: 1 column
      sm: 'repeat(2, 1fr)',         // Tablet: 2 columns
      md: 'repeat(2, 1fr)',         // Small Desktop: 2 columns
      lg: 'repeat(3, 1fr)',         // Desktop: 3 columns
      xl: 'repeat(4, 1fr)',        // Large Desktop (1536px+): 4 columns max
      xxl: 'repeat(4, 1fr)',       // Ultra-wide (2400px+): 4 columns max
    },
    gap: {
      xs: 1.25,     // Mobile: tighter spacing
      sm: 1.5,      // Tablet: standard spacing
      md: 1.5,      // Desktop: standard spacing
      lg: 2,        // Large: slightly more spacing
      xl: 2,        // Very large: balanced spacing for 6 columns
      xxl: 2        // Ultra-wide: balanced spacing for 8 columns
    },
    mb: { xs: 2, sm: 3 },
    // Prevent cards from becoming too wide on ultra-wide screens
    maxWidth: { xl: '100%' },
    width: '100%',
    alignSelf: 'stretch'
  },
  paginationContainer: {
    display: 'flex',
    justifyContent: 'center',
    mt: 3,
    pt: 2,
    borderTop: '1px solid rgba(0,0,0,0.05)'
  },
  mapPlaceholder: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    bgcolor: 'background.white',
    borderRadius: 2
  },
  noResults: {
    textAlign: 'center',
    py: 8,
    color: 'text.secondary'
  }
};

export default Monitoring;
